<!-- src/lib/components/migration/ConnectStep.svelte -->
<!-- Step 2: Enter credentials and validate connection -->
<script lang="ts">
  import type { MigrationSource, ConnectResponse } from '../../types.ts';
  import { api } from '../../api.ts';

  interface Props {
    source: MigrationSource;
    onConnected: (displayName: string) => void;
    onBack: () => void;
  }
  let { source, onConnected, onBack }: Props = $props();

  let token = $state('');
  let baseUrl = $state('');
  let connecting = $state(false);
  let error = $state<string | null>(null);

  const sourceLabel: Record<MigrationSource, string> = {
    linear: 'Linear', plane: 'Plane', asana: 'Asana', figma: 'Figma',
  };

  async function connect() {
    if (!token.trim()) return;
    connecting = true;
    error = null;
    try {
      const body: Record<string, string> = { source_type: source, api_token: token.trim() };
      if (source === 'plane' && baseUrl.trim()) body.base_url = baseUrl.trim();

      const res = await api<ConnectResponse>('/api/migrate/connect', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (res.ok && res.display_name) {
        onConnected(res.display_name);
      } else {
        error = res.error ?? 'Connection failed — check your API token.';
      }
    } catch (e) {
      error = e instanceof Error ? e.message : 'Connection failed.';
    } finally {
      connecting = false;
    }
  }
</script>

<div class="step">
  <h2 class="step-title">Connect to {sourceLabel[source]}</h2>
  <p class="step-hint">Enter your API credentials. These are sent directly to the server and not stored.</p>

  <div class="form">
    <label class="field">
      <span class="field-label">API Token</span>
      <input
        class="field-input"
        type="password"
        placeholder="Paste your API token here"
        bind:value={token}
        disabled={connecting}
      />
    </label>

    {#if source === 'plane'}
      <label class="field">
        <span class="field-label">Base URL</span>
        <input
          class="field-input"
          type="url"
          placeholder="https://app.plane.so"
          bind:value={baseUrl}
          disabled={connecting}
        />
      </label>
    {/if}

    {#if error}
      <p class="error">{error}</p>
    {/if}

    <div class="actions">
      <button class="btn-secondary" onclick={onBack} disabled={connecting}>Back</button>
      <button class="btn-primary" onclick={connect} disabled={connecting || !token.trim()}>
        {#if connecting}
          <span class="spinner" aria-hidden="true"></span> Connecting…
        {:else}
          Connect
        {/if}
      </button>
    </div>
  </div>
</div>

<style>
.step { display: flex; flex-direction: column; gap: 1rem; }
.step-title { margin: 0; font-size: 1.05rem; font-weight: 600; color: var(--text-primary); }
.step-hint { margin: 0; font-size: 0.82rem; color: var(--text-muted); }

.form { display: flex; flex-direction: column; gap: 0.875rem; margin-top: 0.25rem; }

.field { display: flex; flex-direction: column; gap: 0.35rem; }
.field-label { font-size: 0.78rem; font-weight: 500; color: var(--text-secondary); }
.field-input {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
  outline: none;
  transition: border-color 120ms ease;
}
.field-input:focus { border-color: var(--ab-purple-core); }
.field-input:disabled { opacity: 0.5; cursor: not-allowed; }

.error { margin: 0; font-size: 0.78rem; color: var(--ab-red-bright); }

.actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.25rem; }

.btn-primary {
  display: flex; align-items: center; gap: 0.4rem;
  background: oklch(0.72 0.28 310 / 0.12);
  border: 1px solid oklch(0.72 0.28 310 / 0.32);
  color: var(--ab-purple-bright);
  padding: 0.4rem 1.25rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 120ms ease;
}
.btn-primary:hover:not(:disabled) { background: oklch(0.72 0.28 310 / 0.22); }
.btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-secondary {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  color: var(--text-muted);
  padding: 0.4rem 0.875rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 120ms ease;
}
.btn-secondary:hover:not(:disabled) { background: rgba(255,255,255,0.07); }
.btn-secondary:disabled { opacity: 0.4; cursor: not-allowed; }

.spinner {
  display: inline-block;
  width: 12px; height: 12px;
  border: 2px solid rgba(255,255,255,0.2);
  border-top-color: var(--ab-purple-bright);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
